import React, { useState } from 'react';
import { Certification } from '@axiom/const';
import type { Certification as CertificationType } from '@axiom/validation';
import {
  Button,
  CondensedLabel,
  CondensedMedium,
  ContextMenu,
  ContextMenuItem,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  Well,
} from '@axiom/ui';

const qualificationAwards = Object.values(Certification.QUALIFICATION_AWARDS);

const isQualification = (certification: CertificationType) => {
  return qualificationAwards.includes(certification.award);
};

export type CertificationsItemProps = {
  certification: CertificationType;
  onDelete: () => Promise<void>;
  onEdit: () => void;
  name: string;
};

export const CertificationsItem = ({
  certification,
  onDelete,
  onEdit,
  name,
}: CertificationsItemProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  if (!certification.id) return null;

  const cancelDelete = () => {
    setIsConfirmingDelete(false);
  };

  const confirmDelete = async () => {
    await onDelete();
    cancelDelete();
  };

  return (
    <Gutter vertical="8px">
      <Well name={name}>
        <Grid>
          <GridRow verticalAlign="middle">
            <GridColumn width={9}>
              {isQualification(certification) ? (
                <>
                  <CondensedLabel name="INSTITUTION_LABEL">
                    Institution
                  </CondensedLabel>
                  <CondensedMedium name="INSTITUTION_VALUE" maxLines={1}>
                    {certification.institution}
                  </CondensedMedium>
                </>
              ) : (
                <>
                  <CondensedLabel name="AWARD_LABEL">Award</CondensedLabel>
                  <CondensedMedium name="AWARD_VALUE" maxLines={1}>
                    {certification.award}
                  </CondensedMedium>
                </>
              )}
            </GridColumn>
            <GridColumn width={3}>
              <Layout direction="horizontal" position="middle right">
                <LayoutItem name="YEAR">
                  <CondensedLabel>Date</CondensedLabel>
                  <CondensedMedium>{certification.year}</CondensedMedium>
                </LayoutItem>
                <LayoutItem>
                  <Gutter left="8px">
                    <ContextMenu
                      name="CERTIFICATIONITEM_CONTEXT"
                      direction="left"
                    >
                      <ContextMenuItem name="EDIT_MENU_ITEM" onClick={onEdit}>
                        Edit
                      </ContextMenuItem>
                      <ContextMenuItem
                        name="DELETE_MENU_ITEM"
                        onClick={() => {
                          setIsConfirmingDelete(true);
                        }}
                      >
                        Delete
                      </ContextMenuItem>
                    </ContextMenu>
                  </Gutter>
                </LayoutItem>
              </Layout>
            </GridColumn>
          </GridRow>
        </Grid>
        <Gutter top="8px">
          {isQualification(certification) ? (
            <>
              <CondensedLabel>Degree</CondensedLabel>
              <CondensedMedium name="AWARD">
                {certification.award}
              </CondensedMedium>
            </>
          ) : (
            <>
              <CondensedLabel>Awarding Organization</CondensedLabel>
              <CondensedMedium name="INSTITUTION">
                {certification.institution}
              </CondensedMedium>
            </>
          )}
        </Gutter>
      </Well>

      {isConfirmingDelete ? (
        <Modal size="confirm" name="CERTIFICATION_LIST_DELETE_MODAL">
          <ModalHeader
            name="CERTIFICATION_LIST_DELETE_MODAL_HEADER"
            onClose={cancelDelete}
          >
            Delete Qualifications, Certifications, & Awards
          </ModalHeader>
          <ModalSection>
            <Paragraph name="DELETE_CONFIRMATION_TEXT">
              Are you sure you want to delete "{certification.award}" from "
              {certification.institution}"?
            </Paragraph>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_DELETE_BUTTON"
              variation="outline"
              onClick={cancelDelete}
            >
              Cancel
            </Button>
            <Button
              name="CERTIFICATION_LIST_DELETE_BUTTON"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </Gutter>
  );
};
